import React, { FormEvent, useState } from 'react'

import { theme } from '@acorns/grove'
import {
  SelectionList,
  SelectionListOption,
  TextareaInput,
} from '@acorns/web-components'
import { useAnalyticsClient, useRemoteStrings } from '@acorns/web-utils'
import styled from 'styled-components'

import {
  trackAiChatSendFeedbackContainerViewedPayload,
  trackAiChatSendFeedbackOptionButtonTappedPayload,
  trackAiChatSendFeedbackSubmitButtonTappedPayload,
  trackAiChatSendFeedbackSuccessContainerViewedPayload,
} from 'generated/trackingEvents'
import {
  MessageFeedbackReason,
  useRateChatMessageMutation,
} from 'generated/types'
import useAnalyticsComponentViewed from 'hooks/useAnalyticsComponentViewed'

import { StyledButton, StyledDrawer } from './styled-components'
import { SuccessDrawer } from './SuccessDrawer'
import { AI_CHAT_FEEDBACK_ORIGIN } from './utils/constants'

const StyledSelectionList = styled(SelectionList)`
  > div {
    padding: 0 20px;
    border-radius: 10px;
    background-color: rgba(249, 249, 249, 1);
    border: 1px solid rgba(245, 245, 245, 1);
    margin-bottom: 10px;
  }

  > div > div > div {
    font-size: 14px;
  }
`

const StyledTextareaInput = styled(TextareaInput)`
  width: 100%;
`

const RatingDrawer = ({
  isFeedbackDrawerOpen,
  setIsFeedbackDrawerOpen,
  messageId,
}: {
  isFeedbackDrawerOpen: boolean
  setIsFeedbackDrawerOpen: (isFeedbackDrawerOpen: boolean) => void
  messageId: string
}) => {
  const { getRemoteString } = useRemoteStrings()
  const { track } = useAnalyticsClient()
  const [selectedOption, setSelectedOption] = useState<string>('')
  const [isOtherFeedbackOpen, setIsOtherFeedbackOpen] = useState(false)
  const [otherFeedback, setOtherFeedback] = useState<string>('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccessAnimationCompleted, setIsSuccessAnimationCompleted] =
    useState(false)

  const [rateChatMessage] = useRateChatMessageMutation({
    onCompleted: () => {
      setShowSuccess(true)
    },
    onError: () => {
      handleClose()
    },
  })

  const feedbackDrawerRef = useAnalyticsComponentViewed({
    generatedPayload: trackAiChatSendFeedbackContainerViewedPayload({
      origin: AI_CHAT_FEEDBACK_ORIGIN,
    }),
  })

  const handleClose = () => {
    setSelectedOption('')
    setIsOtherFeedbackOpen(false)
    setOtherFeedback('')
    setIsFeedbackDrawerOpen(false)
    setIsSubmitting(false)
    setShowSuccess(false)
    setIsSuccessAnimationCompleted(false)
  }

  const handleAnimationEnd = () => {
    setIsSuccessAnimationCompleted(true)

    if (showSuccess) {
      track(
        ...trackAiChatSendFeedbackSuccessContainerViewedPayload({
          origin: AI_CHAT_FEEDBACK_ORIGIN,
        }),
      )
    }
  }

  const handleSelect = (option: SelectionListOption) => {
    track(
      ...trackAiChatSendFeedbackOptionButtonTappedPayload({
        origin: AI_CHAT_FEEDBACK_ORIGIN,
        ctaTitle: option.value,
      }),
    )
    setSelectedOption(option.value)

    if (option.value === MessageFeedbackReason.Other) {
      setIsOtherFeedbackOpen(true)
    }
  }

  const handleChange = (event: FormEvent<HTMLTextAreaElement>) => {
    setOtherFeedback(event.currentTarget.value)
  }

  const submitFeedback = () => {
    const feedbackReason = selectedOption as MessageFeedbackReason
    const customFeedback =
      feedbackReason === MessageFeedbackReason.Other ? otherFeedback : undefined

    rateChatMessage({
      variables: {
        messageId,
        rating: 0,
        feedbackInput: {
          feedbackReason,
          customFeedback,
        },
      },
    })

    track(
      ...trackAiChatSendFeedbackSubmitButtonTappedPayload({
        origin: AI_CHAT_FEEDBACK_ORIGIN,
      }),
    )

    setIsSubmitting(true)
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submitFeedback()
  }

  if (isSubmitting) {
    return (
      <SuccessDrawer
        isOpen={isFeedbackDrawerOpen}
        showSuccess={showSuccess}
        isSuccessAnimationCompleted={isSuccessAnimationCompleted}
        handleAnimationEnd={handleAnimationEnd}
        handleClose={handleClose}
        messages={{
          successTitle: getRemoteString({
            key: 'acorns_intelligence.chat.feedback_drawer.success.title',
          }),
          successBody: getRemoteString({
            key: 'acorns_intelligence.chat.feedback_drawer.success.body',
          }),
          submittingMessage: getRemoteString({
            key: 'acorns_intelligence.chat.feedback_drawer.submitting',
          }),
        }}
      />
    )
  }

  return (
    <StyledDrawer
      isOpen={isFeedbackDrawerOpen}
      onClose={handleClose}
      title={getRemoteString({
        key: 'acorns_intelligence.chat.feedback_drawer.title',
      })}
      subtext={getRemoteString({
        key: 'acorns_intelligence.chat.feedback_drawer.body',
      })}
      customBreakpoint={theme.dimensions.breakpointsSmall}
    >
      <form onSubmit={handleSubmit} ref={feedbackDrawerRef}>
        {isOtherFeedbackOpen ? (
          <StyledTextareaInput
            id="ai-chat-feedback-other"
            label={getRemoteString({
              key: 'acorns_intelligence.chat.feedback_drawer.additional_feedback.label',
            })}
            name="ai-chat-feedback-other"
            onChange={handleChange}
            placeholder={''}
            value={otherFeedback}
          />
        ) : (
          <StyledSelectionList
            options={[
              {
                label: getRemoteString({
                  key: 'acorns_intelligence.chat.feedback_drawer.inaccurate_information',
                }),
                value: MessageFeedbackReason.InaccurateInformation,
              },
              {
                label: getRemoteString({
                  key: 'acorns_intelligence.chat.feedback_drawer.not_helpful',
                }),
                value: MessageFeedbackReason.NotHelpful,
              },
              {
                label: getRemoteString({
                  key: 'acorns_intelligence.chat.feedback_drawer.offensive',
                }),
                value: MessageFeedbackReason.Offensive,
              },
              {
                label: getRemoteString({
                  key: 'acorns_intelligence.chat.feedback_drawer.other',
                }),
                value: MessageFeedbackReason.Other,
              },
            ]}
            value={selectedOption}
            onSelect={handleSelect}
          />
        )}
        <StyledButton
          onPress={submitFeedback}
          disabled={!selectedOption}
          buttonType="submit"
        >
          {getRemoteString({
            key: 'acorns_intelligence.chat.feedback_drawer.cta',
          })}
        </StyledButton>
      </form>
    </StyledDrawer>
  )
}

export { RatingDrawer }
